import { useEffect, useRef, useState } from 'react';
import type { ConversationSummary, MessageSource } from '@/lib/api';
import { markConversationReadRemote } from '@/lib/api';
import { Avatar, type AvatarChannel } from './Avatar';
import { SwipeableRow, type SwipeAction } from './SwipeableRow';
import { ContactActionSheet, type ContactSheetAction } from './ContactActionSheet';
import { resolveDisplayName, formatPhone } from '@/lib/nameFallback';
import { pillFor, STATUS_PILL_BASE_CLASS } from '@/lib/statusPills';
import {
  isConversationLocallyRead,
  isConversationForceUnread,
  isConversationPinned,
  markConversationReadAndClearUnread,
  markConversationUnread,
  toggleConversationPinned,
} from '@/lib/readState';
import { useMarkResolved } from '@/hooks/useMarkResolved';

interface ConversationRowProps {
  conversation: ConversationSummary;
  onSelect: (id: string) => void;
  /**
   * Tenant slug — required when swipe actions are enabled so the Archive
   * action can fire the markConversationResolved mutation. Optional only
   * for backward compat with any caller that doesn't yet pass it.
   */
  slug?: string;
  /**
   * Fired by the row when the user toggles a state (pin / read / unread)
   * via swipe action. Lets the parent list bump a counter and re-sort
   * pinned items to the top. No-op when omitted.
   */
  onLocalStateChange?: () => void;
  /**
   * Monotonically-increasing counter bumped by the parent whenever
   * localStorage-backed read/unread/pin state changes (e.g. "Mark all
   * read" button). The row re-reads localStorage in a useEffect keyed on
   * this so its `localRead` / `localForceUnread` state syncs without
   * waiting for a remount.
   *
   * Ernie 2026-05-12: previously the row only seeded these from
   * localStorage on mount, so a "Mark all read" tap didn't clear the
   * blue dot until the user navigated away and back.
   */
  stateVersion?: number;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const now = Date.now();
  const diffMs = now - d.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay}d`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// Map the last message's channel to an avatar glyph (Item 3, 2026-06-05).
// classifySource (Worker) returns 'shelby' for CALL message types and 'sms'
// for SMS/MMS/email; 'system' is the rare no-clear-channel fallback. A voice
// thread → phone glyph; everything else → chat-bubble glyph.
function channelFor(source: MessageSource): AvatarChannel {
  return source === 'shelby' ? 'call' : 'text';
}

const iconRead = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const iconUnread = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
  </svg>
);

const iconCall = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const iconArchive = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
    <polyline points="21 8 21 21 3 21 3 8" />
    <rect x="1" y="3" width="22" height="5" />
    <line x1="10" y1="12" x2="14" y2="12" />
  </svg>
);

const iconPin = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
    <line x1="12" y1="17" x2="12" y2="22" />
    <path d="M5 17h14l-1.6-2.13A4 4 0 0 1 16.6 12.5V7h.4a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h.4v5.5a4 4 0 0 1-.8 2.37L5 17z" />
  </svg>
);

// Smaller filled pin shown inline next to a pinned contact's name.
const iconPinIndicator = (
  <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" className="h-3 w-3 flex-shrink-0">
    <path d="M16 4v3.6a3 3 0 0 0 .9 2.13L18.5 11H17a1 1 0 0 0-1 1v.5l-3 3-3-3V12a1 1 0 0 0-1-1H7.5l1.6-1.27A3 3 0 0 0 10 7.6V4H8a1 1 0 0 1 0-2h8a1 1 0 0 1 0 2h-2zM12 16l-1 6h2l-1-6z" />
  </svg>
);

export function ConversationRow({
  conversation,
  onSelect,
  slug,
  onLocalStateChange,
  stateVersion,
}: ConversationRowProps) {
  const { id, contact_name, contact_phone, last_message, unread, status, is_workflow_active, has_pending_reminder } = conversation;
  const time = formatTimestamp(last_message.timestamp_iso);

  // Resolve a premium-feel display name. "Unknown" and other placeholders
  // get replaced with smart fallbacks ("New CT Lead", "Ceramic Coating
  // Lead") so the list never looks broken (R11 consult 2026-05-06).
  const { displayName, isFallback } = resolveDisplayName({
    contactName: contact_name,
    contactPhone: contact_phone,
  });
  const phoneDisplay = formatPhone(contact_phone);

  // Status pill. Real statuses (Needs you / Quoted / Booked / Archived) win;
  // otherwise the last message's source + direction label the channel
  // (Inbound call / text / msg) instead of the old generic "AI handled".
  const pill = pillFor(status, last_message.source, last_message.direction);

  // Local override flips so the row UI updates instantly when the user uses
  // a swipe action (mark read / unread / pin) without waiting for the
  // parent's React Query refetch. We seed from localStorage on mount.
  const [localRead, setLocalRead] = useState(() =>
    isConversationLocallyRead(id, last_message.timestamp_iso),
  );
  const [localForceUnread, setLocalForceUnread] = useState(() =>
    isConversationForceUnread(id),
  );
  const [pinned, setPinned] = useState(() => isConversationPinned(id));

  // Re-sync from localStorage whenever the parent bumps stateVersion
  // (e.g. "Mark all read" tap). Without this the row's blue dot stayed
  // visible until the row unmounted + remounted on tab nav, since
  // useState only seeded on initial mount. Ernie 2026-05-12.
  useEffect(() => {
    setLocalRead(isConversationLocallyRead(id, last_message.timestamp_iso));
    setLocalForceUnread(isConversationForceUnread(id));
    setPinned(isConversationPinned(id));
  }, [stateVersion, id, last_message.timestamp_iso]);

  // Effective unread = (Worker says unread OR user force-marked unread) AND
  // user hasn't locally read it.
  const effectiveUnread = (unread || localForceUnread) && !localRead;

  const archiveMutation = useMarkResolved(slug ?? '', id);

  // --- Long-press detection ----------------------------------------------
  // 500ms hold with no significant movement triggers the action sheet.
  // Using touch + pointer events for cross-device coverage.
  const [sheetOpen, setSheetOpen] = useState(false);
  const longPressTimer = useRef<number | null>(null);
  const longPressStart = useRef<{ x: number; y: number } | null>(null);
  const longPressTriggered = useRef(false);
  const LONG_PRESS_MS = 500;
  const LONG_PRESS_TOLERANCE_PX = 8;

  const cancelLongPress = () => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressStart.current = null;
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    longPressTriggered.current = false;
    longPressStart.current = { x: e.clientX, y: e.clientY };
    longPressTimer.current = window.setTimeout(() => {
      longPressTriggered.current = true;
      setSheetOpen(true);
      // iOS haptic where supported
      if ('vibrate' in navigator) {
        try { navigator.vibrate(15); } catch { /* ignore */ }
      }
    }, LONG_PRESS_MS);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!longPressStart.current) return;
    const dx = Math.abs(e.clientX - longPressStart.current.x);
    const dy = Math.abs(e.clientY - longPressStart.current.y);
    if (dx > LONG_PRESS_TOLERANCE_PX || dy > LONG_PRESS_TOLERANCE_PX) {
      cancelLongPress();
    }
  };

  const handleActivate = (e: React.MouseEvent) => {
    if (longPressTriggered.current) {
      // Long-press fired the sheet; suppress the click that would otherwise
      // navigate into the conversation.
      e.preventDefault();
      e.stopPropagation();
      longPressTriggered.current = false;
      return;
    }
    onSelect(id);
  };

  // --- Swipe action handlers ---------------------------------------------

  const handleSwipeMarkRead = () => {
    const now = Date.now();
    markConversationReadAndClearUnread(id, new Date(now).toISOString());
    setLocalRead(true);
    setLocalForceUnread(false);
    // Persist server-side so the read mark survives refetch + other devices.
    if (slug) void markConversationReadRemote(slug, id, now).catch(() => {});
    onLocalStateChange?.();
  };

  const handleSwipeMarkUnread = () => {
    markConversationUnread(id);
    setLocalRead(false);
    setLocalForceUnread(true);
    onLocalStateChange?.();
  };

  const handleSwipeCall = () => {
    if (!contact_phone) return;
    window.location.href = `tel:${contact_phone}`;
  };

  const handleSwipeArchive = () => {
    if (!slug) return;
    archiveMutation.mutate({});
  };

  const handleSwipePin = () => {
    const next = toggleConversationPinned(id);
    setPinned(next);
    onLocalStateChange?.();
  };

  // --- Build the action arrays -------------------------------------------

  const leftActions: SwipeAction[] = [
    {
      label: pinned ? 'Unpin' : 'Pin',
      icon: iconPin,
      bgClass: 'bg-amber-500 text-white',
      onAction: handleSwipePin,
    },
    effectiveUnread
      ? {
          label: 'Read',
          icon: iconRead,
          bgClass: 'bg-ss-blue text-white',
          onAction: handleSwipeMarkRead,
        }
      : {
          label: 'Unread',
          icon: iconUnread,
          bgClass: 'bg-ss-blue text-white',
          onAction: handleSwipeMarkUnread,
        },
  ];

  const rightActions: SwipeAction[] = [];
  if (contact_phone) {
    rightActions.push({
      label: 'Call',
      icon: iconCall,
      bgClass: 'bg-emerald-500 text-white',
      onAction: handleSwipeCall,
    });
  }
  if (slug) {
    rightActions.push({
      label: 'Archive',
      icon: iconArchive,
      bgClass: 'bg-slate-500 text-white',
      onAction: handleSwipeArchive,
    });
  }

  // --- Long-press action sheet content -----------------------------------
  const sheetActions: ContactSheetAction[] = [];
  if (contact_phone) {
    sheetActions.push({
      label: 'Call',
      icon: iconCall,
      onAction: handleSwipeCall,
    });
    sheetActions.push({
      label: 'Copy phone number',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      ),
      onAction: () => {
        if (!contact_phone) return;
        try {
          void navigator.clipboard?.writeText(contact_phone);
        } catch { /* ignore */ }
      },
    });
  }
  sheetActions.push({
    label: pinned ? 'Unpin from top' : 'Pin to top',
    icon: iconPin,
    onAction: handleSwipePin,
  });
  sheetActions.push({
    label: effectiveUnread ? 'Mark as read' : 'Mark as unread',
    icon: effectiveUnread ? iconRead : iconUnread,
    onAction: effectiveUnread ? handleSwipeMarkRead : handleSwipeMarkUnread,
  });
  if (slug) {
    sheetActions.push({
      label: 'Archive',
      icon: iconArchive,
      onAction: handleSwipeArchive,
    });
  }

  const rowContent = (
    <button
      type="button"
      onClick={handleActivate}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={cancelLongPress}
      onPointerCancel={cancelLongPress}
      onPointerLeave={cancelLongPress}
      onContextMenu={(e) => e.preventDefault()}
      className={[
        'flex w-full min-h-[68px] items-stretch gap-3 border-b border-slate-100 dark:border-[#1E293B] px-4 py-3.5 text-left',
        // Tinted background when pinned so the user can spot pinned rows
        // even after the list has been scrolled past the top group.
        pinned
          ? 'bg-amber-50/60 dark:bg-amber-950/20'
          : 'bg-white dark:bg-[#0F172A]',
        'transition-all duration-150 ease-ss-standard',
        'active:bg-ink-50 dark:active:bg-[#1E293B] active:scale-[0.995]',
        'focus:outline-none focus-visible:bg-ink-50 dark:focus-visible:bg-[#1E293B]',
      ].join(' ')}
      style={{ touchAction: 'manipulation' }}
      aria-label={`Open conversation with ${displayName}. Long-press for actions.`}
    >
      <Avatar name={displayName} channel={channelFor(last_message.source)} />
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        {/* Top line: name + time. Name uses normal weight for fallback labels
            so they read as labels, not real names. */}
        <div className="mb-0.5 flex items-baseline justify-between gap-2">
          <span
            className={[
              'flex items-center gap-1 truncate text-[15px] text-ink-900 dark:text-[#E2E8F0]',
              effectiveUnread ? 'font-bold' : isFallback ? 'font-medium italic' : 'font-semibold',
            ].join(' ')}
          >
            {pinned ? (
              <span className="text-amber-500" aria-label="Pinned" title="Pinned">
                {iconPinIndicator}
              </span>
            ) : null}
            <span className="truncate">{displayName}</span>
          </span>
          <span className="flex-shrink-0 text-xs text-ink-400 dark:text-[#64748B]">
            {time}
          </span>
        </div>
        {/* Status row: pill + optional Drip pill + phone (if fallback name)
            — replaces the all-caps SMS badge. */}
        <div className="mb-0.5 flex min-w-0 items-center gap-2">
          <span className={[STATUS_PILL_BASE_CLASS, pill.className].join(' ')}>
            {pill.label}
          </span>
          {is_workflow_active ? (
            <span
              className={[
                STATUS_PILL_BASE_CLASS,
                'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
              ].join(' ')}
              title="Auto follow-up workflow is active for this contact. Tap Take Over to stop it."
            >
              Drip
            </span>
          ) : null}
          {has_pending_reminder ? (
            <span
              className="flex-shrink-0 text-amber-500 dark:text-amber-400"
              aria-label="Follow-up reminder set"
              title="Follow-up reminder set — open the conversation to view or edit."
            >
              {/* 2026-07-02 (Part A) — small clock, matches the pin-indicator
                  scale. Set server-side (has_pending_reminder) from the
                  reminders table. */}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                <circle cx="12" cy="13" r="8" />
                <path d="M12 9v4l2.5 1.5" />
                <path d="M5 3 2 6" />
                <path d="m22 6-3-3" />
              </svg>
            </span>
          ) : null}
          {isFallback && phoneDisplay ? (
            <span className="truncate text-[12px] text-ink-400 dark:text-[#64748B]">
              {phoneDisplay}
            </span>
          ) : null}
        </div>
        {/* Snippet line — wraps two lines via line-clamp for richer preview. */}
        <div
          className={[
            'min-w-0 overflow-hidden text-[13px] leading-snug',
            effectiveUnread
              ? 'font-medium text-ink-900 dark:text-[#E2E8F0]'
              : 'text-ink-500 dark:text-[#94A3B8]',
          ].join(' ')}
        >
          <span className="line-clamp-1">{last_message.preview}</span>
        </div>
      </div>
      {effectiveUnread ? (
        <span
          aria-label="Unread"
          className="my-auto h-2 w-2 flex-shrink-0 self-center rounded-full bg-ss-blue"
        />
      ) : null}
    </button>
  );

  return (
    <>
      <SwipeableRow leftActions={leftActions} rightActions={rightActions}>
        {rowContent}
      </SwipeableRow>
      <ContactActionSheet
        open={sheetOpen}
        contactName={contact_name}
        contactPhone={contact_phone}
        actions={sheetActions}
        onClose={() => setSheetOpen(false)}
      />
    </>
  );
}
